/**
 * Keeps a login alive: a login token lasts an hour, so when a call comes back 401 the refresh token (7 days, from
 * /login or /accept-invite) is swapped for a new one at POST /refresh and the call is sent again, once. Calls that fail
 * together share one refresh. The new token only goes to localStorage, where every API call reads it - the app's state is
 * left alone, so nothing re-mounts mid-work. When the refresh is refused (expired, password changed, removed) `onExpired`
 * signs out.
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL;
const SKIP = ['/login', '/refresh', '/request-password-reset', '/reset-password', '/accept-invite'];

let pending = null;

const renew = () => {
  if (!pending) {
    const refreshToken = localStorage.getItem('refreshToken');
    pending = (refreshToken
      ? axios.post(`${API_BASE}/refresh`, { refresh_token: refreshToken }).then((res) => res.data.token)
      : Promise.reject(new Error('No refresh token'))
    ).finally(() => { pending = null; });
  }
  return pending;
};

export default function setupTokenRefresh({ onExpired }) {
  const id = axios.interceptors.response.use(
    (res) => res,
    async (error) => {
      const original = error.config;
      const sentToken = original?.headers?.Authorization || original?.headers?.authorization;
      if (error.response?.status !== 401 || !original || original._retried || !sentToken
          || SKIP.some((path) => (original.url || '').endsWith(path))) {
        return Promise.reject(error);
      }
      original._retried = true;
      let token;
      try {
        token = await renew();
      } catch {
        onExpired();
        return Promise.reject(error);
      }
      localStorage.setItem('token', token);
      original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
      return axios(original);
    },
  );
  return () => axios.interceptors.response.eject(id);
}
